import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { useFeaturesContext } from 'hooks/FeaturesContext'
import useWarrantyInfo from 'hooks/useWarrantyInfo'
import useSearchParams from 'hooks/useSearchParams'
import useServerlessWarrantyLookup from 'hooks/useServerlessWarrantyLookup'

import Search from 'components/Search'
import Spinner from 'components/Spinner'

import WarrantyInfo from 'pages/shared/WarrantyInfo'

import GA4 from 'helpers/ga4'

import { PageTitle, PageHeader, SinglePanePage, EmptyListMessage, screenSize } from 'styles/shared'

import { color } from 'styles/colors'

const WarrantyLookup = () => {
  const navigate = useNavigate()

  const query = useSearchParams('q')

  const { enabledFeatures } = useFeaturesContext()
  const isServerlessWarrantyEnabled = enabledFeatures?.includes('warranty')

  const [searchInputValue, setSearchInputValue] = useState<string>(query)
  const [searchedValue, setSearchedValue] = useState<string | null>(query)
  const [showSpinner, setShowSpinner] = useState(false)
  const { warrantyInfo, isLoaded, hasError } = useWarrantyInfo(
    searchedValue || '',
    false,
    isServerlessWarrantyEnabled
  )
  const { warranty: serverlessWarranty, isWarrantyLoaded } = useServerlessWarrantyLookup(searchedValue || '')

  const onSearchCallback = useCallback((query: string) => {
    setShowSpinner(true)
    if (query.length) {
      setSearchedValue(query)
    }

    GA4.sendEvent({ category: 'Warranty Lookup', action: 'Clicked on Search' })

    navigate(`/warranty_lookup${query ? `?q=${query}` : ''}`)
    setShowSpinner(false)
  }, [navigate])

  const onSearchValueChange = useCallback((value: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const start = input.selectionStart
    const end = input.selectionEnd

    setSearchInputValue(value.toUpperCase())

    // restore cursor position
    setTimeout(() => {
      input.selectionStart = start
      input.selectionEnd = end
    }, 0)
  }, [])

  const clearSearch = useCallback(() => {
    setSearchInputValue('')
    setSearchedValue(null)
    setShowSpinner(false)
    navigate('/warranty_lookup')
  }, [navigate])

  const renderContent = useCallback(() => {
    if (!searchedValue) {
      return null
    }

    if (
      (!enabledFeatures?.includes('warranty') && !isLoaded) ||
      (enabledFeatures?.includes('warranty') && !isWarrantyLoaded)
    ) {
      return <Spinner variant="page" />
    } else if (hasError) {
      return (
        <EmptyListMessage>
          <h1>Server Error</h1>
          <h2>An error occurred attempting to retrieve the warranty information</h2>
        </EmptyListMessage>
      )
    } else if ((!warrantyInfo && !serverlessWarranty) || (enabledFeatures?.includes('warranty') && !serverlessWarranty)) {
      return (
        <EmptyListMessage>
          <h1>No Results</h1>
          <h2>No warranty information found</h2>
        </EmptyListMessage>
      )
    } else {
      let warrantyData
      if (enabledFeatures?.includes('warranty')) {
        warrantyData = serverlessWarranty
      } else {
        warrantyData = warrantyInfo
      }

      return (
        <S.CardContainer>
          <WarrantyInfo
            serialNumber={searchedValue}
            warrantyInfo={warrantyData}
            displaySalesOrder={!enabledFeatures?.includes('warranty')}
          />
        </S.CardContainer>
      )
    }
  }, [searchedValue, isLoaded, hasError, warrantyInfo, isWarrantyLoaded, serverlessWarranty, enabledFeatures])

  return (
    <S.Container>
      <PageHeader>
        <PageTitle>Warranty Lookup</PageTitle>
        <S.Search
          value={searchInputValue}
          focusOnMount
          placeholder="Search by serial number"
          ariaLabel="warranty lookup"
          onChange={onSearchValueChange}
          onSearch={onSearchCallback}
          onClickClearIcon={clearSearch}
          showSpinner={showSpinner}
        />
      </PageHeader>
      <S.SinglePanePage>
        {renderContent()}
      </S.SinglePanePage>
    </S.Container>
  )
}

export default WarrantyLookup

const S = {
  Container: styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-top: -5px;
  `,
  CardContainer: styled.div`
    margin: 20px;
  `,
  SinglePanePage: styled(SinglePanePage)`
    @media ${screenSize.small} {
      margin-top: 0;
    }
  `,
  Search: styled(Search)`
    max-width: 500px;
  `,
  EmptyListMessage: styled(EmptyListMessage)`
    color: ${color('helpText')};
    margin-top: 5em;
  `
}
